import { useState, useEffect, useRef } from "react";
import { Check, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { trackFormSubmit } from "@/utils/analytics";

const LeadFormModal = ({ open, onClose, type }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    business: "",
    hasWebsite: "",
    websiteUrl: "",
    notes: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const turnstileContainerId = useRef(`turnstile-${Date.now()}`);
  const widgetIdRef = useRef(null);

  // Render Turnstile widget when modal opens
  useEffect(() => {
    if (open && !submitted) {
      const renderTurnstile = () => {
        const container = document.getElementById(turnstileContainerId.current);
        
        if (container && window.turnstile) {
          if (widgetIdRef.current) {
            try {
              window.turnstile.reset(widgetIdRef.current);
            } catch (e) {
              try {
                window.turnstile.remove(widgetIdRef.current);
              } catch (e2) {}
              widgetIdRef.current = null;
            }
          }
          
          if (!widgetIdRef.current) {
            try {
              widgetIdRef.current = window.turnstile.render(container, {
                sitekey: '0x4AAAAAACWaMNL0v-e3B3QS',
                callback: (token) => {
                  setTurnstileToken(token);
                  setTurnstileLoaded(true);
                  setError("");
                },
                'error-callback': () => {
                  setError("Ellenőrzés sikertelen. Kérjük, próbáld újra.");
                  setTurnstileToken("");
                },
                'expired-callback': () => {
                  setTurnstileToken("");
                  setTurnstileLoaded(false);
                },
                theme: 'dark',
                size: 'normal'
              });
            } catch (e) {
              console.error('Turnstile render error:', e);
            }
          }
        }
      };

      const timer1 = setTimeout(renderTurnstile, 500);
      const timer2 = setTimeout(renderTurnstile, 1500);
      const timer3 = setTimeout(renderTurnstile, 3000);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [open, submitted]);

  // Cleanup widget when modal closes
  useEffect(() => {
    if (!open) {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {}
        widgetIdRef.current = null;
      }
      setSubmitted(false);
      setError("");
      setIsSubmitting(false);
      setTurnstileToken("");
      setTurnstileLoaded(false);
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!turnstileToken) {
      setError("Kérjük, végezd el a biztonsági ellenőrzést a küldés előtt.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://landinglab.akcios.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          profession: formData.business,
          hasWebsite: formData.hasWebsite === 'yes',
          websiteUrl: formData.websiteUrl,
          message: formData.notes,
          formType: type,
          turnstileToken: turnstileToken,
          timestamp: new Date().toISOString()
        })
      });

      let data = null;
      try {
        data = await response.json();
      } catch (e) {}
      
      if (!response.ok) {
        const errorMsg = data?.error || `Hiba történt (${response.status})`;
        throw new Error(errorMsg);
      }

      trackFormSubmit(type);
      setSubmitted(true);
      
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: "", email: "", phone: "", business: "", hasWebsite: "", websiteUrl: "", notes: "" });
        setTurnstileToken("");
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current);
        }
        onClose();
      }, 3000);

    } catch (err) {
      if (err.message.includes('Spam') || err.message.includes('Turnstile')) {
        setError("Biztonsági ellenőrzés sikertelen. Kérjük, próbáld újra!");
      } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError("Hálózati hiba. Kérjük, ellenőrizd az internetkapcsolatod.");
      } else {
        setError(err.message || "Hiba történt. Kérjük, próbáld újra később.");
      }
      setTurnstileToken("");
      setTurnstileLoaded(false);
      if (window.turnstile && widgetIdRef.current) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch (e) {}
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-ink border border-white/10 text-on-dark max-w-lg pb-24 sm:pb-6">
        <DialogHeader>
          <DialogTitle className="font-sora text-xl sm:text-2xl">
            {type === "sketch" ? "Kérek egy vázlatot" : "Kérek egy gyors ajánlatot"}
          </DialogTitle>
          <DialogDescription className="text-on-dark/60 text-sm">
            {type === "sketch"
              ? "Írd le röviden a vállalkozásod, és küldünk egy landing irányt."
              : "Adj meg pár adatot, és hamarosan jelentkezünk."}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-3">
              <Check size={24} className="text-accent" />
            </div>
            <p className="text-on-dark font-medium">Köszönöm!</p>
            <p className="text-on-dark/60 mt-1 text-sm">Üzenet sikeresen elküldve...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div>
              <label className="block text-sm text-on-dark/80 mb-1">Név</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-on-dark placeholder:text-on-dark/30 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all outline-none text-sm"
                placeholder="Kovács János"
                data-testid="form-name"
              />
            </div>

            <div>
              <label className="block text-sm text-on-dark/80 mb-1">Email cím</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-on-dark placeholder:text-on-dark/30 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all outline-none text-sm"
                placeholder="pelda@email.hu"
                data-testid="form-email"
              />
            </div>

            <div>
              <label className="block text-sm text-on-dark/80 mb-1">Telefonszám</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-on-dark placeholder:text-on-dark/30 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all outline-none text-sm"
                placeholder="+36 30 123 4567"
                data-testid="form-phone"
              />
            </div>

            <div>
              <label className="block text-sm text-on-dark/80 mb-1">Mivel foglalkozol?</label>
              <input
                type="text"
                required
                value={formData.business}
                onChange={(e) => setFormData({ ...formData, business: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-on-dark placeholder:text-on-dark/30 focus:border-primary/50 focus:ring-primary/50 transition-all outline-none text-sm"
                placeholder="pl. Klímaszerelés, Fodrász, Könyvelő"
                data-testid="form-business"
              />
            </div>

            <div>
              <label className="block text-sm text-on-dark/80 mb-1">Van-e jelenleg weboldalad?</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasWebsite"
                    value="yes"
                    checked={formData.hasWebsite === "yes"}
                    onChange={(e) => setFormData({ ...formData, hasWebsite: e.target.value })}
                    className="w-4 h-4 accent-primary"
                    data-testid="form-has-website-yes"
                  />
                  <span className="text-on-dark/80 text-sm">Van</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasWebsite"
                    value="no"
                    checked={formData.hasWebsite === "no"}
                    onChange={(e) => setFormData({ ...formData, hasWebsite: e.target.value, websiteUrl: "" })}
                    className="w-4 h-4 accent-primary"
                    data-testid="form-has-website-no"
                  />
                  <span className="text-on-dark/80 text-sm">Nincs</span>
                </label>
              </div>
            </div>

            {formData.hasWebsite === "yes" && (
              <div>
                <label className="block text-sm text-on-dark/80 mb-1">Weboldal címe</label>
                <input
                  type="text"
                  value={formData.websiteUrl}
                  onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-on-dark placeholder:text-on-dark/30 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all outline-none text-sm"
                  placeholder="pelda.hu"
                  data-testid="form-website-url"
                />
              </div>
            )}

            <div>
              <label className="block text-sm text-on-dark/80 mb-1">Üzenet/Egyéb tudnivaló</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-on-dark placeholder:text-on-dark/30 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all outline-none resize-none text-sm"
                placeholder="Bármi egyéb..."
                data-testid="form-notes"
              />
            </div>

            {/* Cloudflare Turnstile widget */}
            <div className="flex justify-center">
              <div id={turnstileContainerId.current} data-testid="turnstile-widget"></div>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-gradient-to-r from-primary to-accent text-white rounded-full py-3 font-semibold transition-all ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              data-testid="form-submit"
            >
              {isSubmitting ? (
                <>
                  <svg className="inline mr-2 w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Küldés...
                </>
              ) : (
                <>
                  <Send className="inline mr-2" size={18} />
                  Küldés
                </>
              )}
            </button>

            <p className="text-center text-xs text-on-dark/40">
              Nem spam. Csak a válaszhoz használjuk.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadFormModal;
